import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './DocumentDetails.css';

const DocumentDetails = ({ documentId, onClose }) => {
  const { token, isAuthenticated } = useAuth();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to get proper authentication headers
  const getAuthHeaders = () => {
    // Priority: 1. User JWT token, 2. Environment variable, 3. Default fallback
    const authToken = (isAuthenticated() && token) 
      ? token 
      : (process.env.REACT_APP_API_AUTH_TOKEN || '12345');
      
    return {
      'Authorization': `Bearer ${authToken}`
    };
  };

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await axios.get(`/documents/${documentId}`, {
          headers: getAuthHeaders()
        });
        if (response.status === 200) {
          setDocument(response.data);
          setLoading(false);
        } else {
          setError('Error fetching document: ' + response.statusText);
          setLoading(false);
        }
      } catch (err) {
        setError('Error fetching document: ' + err.message);
        setLoading(false);
      }
    };

    if (documentId) {
      fetchDocument();
    }
  }, [documentId]);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h2>Document Details</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <p>Loading document details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h2>Document Details</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <div className="error-message">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Document Details - ID: {document.document_id}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="document-content-layout">
            <div className="document-preview-section">
              <h3>Document Preview</h3>
              {document.file_path ? (
                <iframe
                  src={`/documents/${document.document_id}/preview?auth=${encodeURIComponent((isAuthenticated() && token) ? token : (process.env.REACT_APP_API_AUTH_TOKEN || '12345'))}`}
                  title="Document Preview"
                  className="document-preview-iframe"
                  onLoad={() => console.log('Preview loaded')}
                  onError={() => console.log('Preview failed to load')}
                >
                  Your browser does not support iframes.
                </iframe>
              ) : (
                <div className="no-preview-message">
                  No document file available for preview
                </div>
              )}
            </div>
            <div className="document-info-section">
              <h3>Document Information</h3>
              <div className="document-details">
                <div className="detail-row">
                  <label>Title:</label>
                  <span>{document.title}</span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status-badge status-${document.status.toLowerCase()}`}>
                    {document.status}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Form Type:</label>
                  <span>{document.form_type}</span>
                </div>
                <div className="detail-row">
                  <label>Uploader ID:</label>
                  <span>{document.uploader_id}</span>
                </div>
                <div className="detail-row">
                  <label>Upload Date:</label>
                  <span>{formatDate(document.upload_date)}</span>
                </div>
                <div className="detail-row">
                  <label>Last Modified:</label>
                  <span>{formatDate(document.last_modified)}</span>
                </div>
                <div className="detail-row">
                  <label>File Path:</label>
                  <span>{document.file_path || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Processed Type:</label>
                  <span>{document.processed_type || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Processing Time:</label>
                  <span>{document.processing_time ? `${document.processing_time}ms` : 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>External Reference ID:</label>
                  <span>{document.external_reference_id || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Last Modified By:</label>
                  <span>{document.last_modified_by || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentDetails;